import { decodeZoneOptionId } from '../../../utils/scopeSelection';
import { describeHttpError } from '../../../utils/apiError';

/*
 * GANTT02 + FILTER-UX01 (ТЗ v4 §19.1–19.2, FE-FIX16).
 *
 * Чистая логика вкладки Ганта: какие панели следуют из checkbox-выбора, какой
 * запрос уходит на каждую и как читается ответ gantt-v2. Здесь нет ни одного
 * сложения данных разных камер: несколько выбранных scope дают несколько
 * независимых панелей и запросов, а не общую сумму (§19.2 прямо это запрещает).
 */

/** Панелей за раз — ограниченный набор, а не «сколько выбрали» (§19.2: no request storm). */
export const MAX_GANTT_PANELS = 12;

/** Одновременных запросов в группе; остальные ждут очереди. */
export const GANTT_REQUEST_CONCURRENCY = 3;

export const GANTT_MODES = [
  { id: 'raw', label: 'Кадры', hint: 'Каждое наблюдение отдельной отметкой' },
  { id: 'compressed', label: 'Интервалы', hint: 'События, сгруппированные по интервалам' },
];

/**
 * Панели = камеры × выбранные зоны этой камеры. Камера без выбранных зон даёт
 * одну панель по всей камере (zone_id не отправляется вовсе).
 *
 * Возвращается и `total`: при обрезке до MAX_GANTT_PANELS пользователь должен
 * видеть, что показаны не все выбранные ветви.
 */
export const buildGanttTargets = (cameras, zoneIds = [], zoneOptions = []) => {
  const zoneNames = new Map((zoneOptions || []).map((option) => [option.id, option.name]));
  const zonesByCamera = new Map();
  (zoneIds || []).forEach((value) => {
    const { cameraId, zoneId } = decodeZoneOptionId(value);
    if (!cameraId || !zoneId) return;
    const list = zonesByCamera.get(cameraId) || [];
    list.push({ zoneId, name: zoneNames.get(value) || zoneId });
    zonesByCamera.set(cameraId, list);
  });

  const all = (cameras || []).flatMap((camera) => {
    const zones = zonesByCamera.get(camera.id) || [];
    if (zones.length === 0) {
      return [{
        key: String(camera.id),
        cameraId: camera.id,
        cameraName: camera.name,
        parentName: camera.parentName || '',
        zoneId: null,
        zoneName: '',
      }];
    }
    return zones.map((zone) => ({
      key: `${camera.id}:${zone.zoneId}`,
      cameraId: camera.id,
      cameraName: camera.name,
      parentName: camera.parentName || '',
      zoneId: zone.zoneId,
      zoneName: zone.name,
    }));
  });

  return {
    targets: all.slice(0, MAX_GANTT_PANELS),
    total: all.length,
    truncated: all.length > MAX_GANTT_PANELS,
  };
};

export const describeGanttTarget = (target) =>
  (target?.zoneId ? `${target.cameraName} · ${target.zoneName}` : target?.cameraName || '');

/*
 * Потолок точек raw-режима. Server default — 5000: камера с кадром раз в 3 с
 * упиралась в него уже за ~4 часа, и одна-единственная панель получала «Too
 * many points». 20000 — максимум, который принимает gantt-v2.
 */
export const GANTT_RAW_MAX_POINTS = 20000;

/** Query gantt-v2. Пустые значения не отправляются — у них есть server default. */
export const buildGanttParams = ({ metric, fromIso, toIso, zoneId, mode, bucketSeconds }) => {
  const params = { metric, from: fromIso, to: toIso, mode };
  if (mode === 'raw') params.max_points = GANTT_RAW_MAX_POINTS;
  if (zoneId) params.zone_id = zoneId;
  if (mode === 'compressed' && bucketSeconds) params.bucket_seconds = Number(bucketSeconds);
  return params;
};

/** 422 — guard объёма gantt-v2 («too many points»). */
export const isGanttLimitError = (error) => error?.response?.status === 422;

/** Отменённый запрос — не ошибка панели, а нормальный конец прежней группы. */
export const isAbortError = (error) =>
  error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED' || error?.name === 'AbortError';

/**
 * §19.2: guard'ы raw/compressed показываются как ограничение сервера. Период
 * молча не дробится — пользователь сам решает, сузить интервал или перейти к
 * агрегированному режиму.
 */
export const describeGanttLimit = (error) => {
  if (error?.response?.status === 422) {
    return {
      limit: true,
      text: 'Слишком много данных для одного запроса.',
      hint: 'Сократите период или переключитесь в режим «Интервалы».',
    };
  }
  return { limit: false, text: describeHttpError(error, 'Не удалось загрузить данные'), hint: '' };
};

const numericOf = (value) => {
  if (value === true) return 1;
  if (value === false) return 0;
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Ответ → отрезки общей оси. Позиция считается по абсолютному времени
 * (observed_at / from_at): ось у всех панелей одна и та же
 * (synchronization_basis = absolute_utc_interval), а display_* нужен только для
 * подписей в зоне камеры.
 *
 * Конец raw-отрезка — начало следующего наблюдения; у последнего конца нет, и
 * рисуется он минимальной шириной. Придумывать длительность последнего кадра
 * нельзя: наблюдение — это точка, а не интервал.
 */
export const ganttSegments = (response) => {
  if (!response) return [];

  if (response.mode === 'compressed') {
    return (response.buckets || [])
      .map((bucket) => ({
        startMs: Date.parse(bucket.from_at),
        endMs: Date.parse(bucket.to_at),
        atIso: bucket.from_at,
        value: numericOf(bucket.numeric_avg != null ? bucket.numeric_avg : bucket.last_value),
        state: bucket.valid_samples > 0 ? 'valid' : 'unknown',
        samples: bucket.samples,
      }))
      .filter((segment) => Number.isFinite(segment.startMs));
  }

  const points = (response.points || [])
    .map((point) => ({
      startMs: Date.parse(point.observed_at),
      atIso: point.observed_at,
      value: numericOf(point.value),
      state: point.state === 'unknown' ? 'unknown' : 'valid',
      samples: 1,
    }))
    .filter((point) => Number.isFinite(point.startMs))
    .sort((a, b) => a.startMs - b.startMs);

  return points.map((point, index) => ({
    ...point,
    endMs: index + 1 < points.length ? points[index + 1].startMs : null,
  }));
};

/** Значения разных панелей не смешиваются: шкала цвета считается по своей панели. */
export const panelValueMax = (segments) =>
  (segments || []).reduce((max, segment) => (
    segment.state === 'valid' && segment.value != null && segment.value > max ? segment.value : max
  ), 0);

/**
 * Bounded loading: задачи идут пачками по `limit`, а не все сразу. Промис
 * завершается, когда обработаны все задачи (ошибки обрабатывает сам worker —
 * группа не падает из-за одной панели).
 */
export const runWithConcurrency = async (items, limit, worker) => {
  const queue = [...items];
  const size = Math.max(1, Math.min(limit, queue.length));
  const runners = Array.from({ length: size }, async () => {
    for (let next = queue.shift(); next !== undefined; next = queue.shift()) {
      // eslint-disable-next-line no-await-in-loop
      await worker(next);
    }
  });
  await Promise.all(runners);
};
